/**
 * Appointment History Import Service
 * API integration for importing pre-system ASO/exam history from spreadsheets
 */
import apiClient from './apiClient';

export type { UnresolvedTerm, AvailableOption, ImportPreviewResult } from '../types/import';
import type { ImportPreviewResult, ImportedRecordRef } from '../types/import';

export interface AppointmentHistoryImportMappings {
  nome_exame?: Record<string, string>;
  [key: string]: Record<string, string | number> | undefined;
}

export interface AppointmentHistoryImportOptions {
  companyId?: number;
  mappings?: AppointmentHistoryImportMappings;
  autoCreateCatalogEntries?: boolean;
  /** Deixa a IA (Gemini) extrair os dados de qualquer layout de planilha, em vez de exigir os
   *  nomes de coluna exatos do template. Sem efeito se a IA estiver desligada/sem chave. */
  useAiExtraction?: boolean;
}

export interface PdfImportResult {
  fileName: string;
  success: boolean;
  appointmentId?: number;
  employeeName?: string;
  error?: string;
}

const extractData = <T>(response: { data?: { data?: T } | T }): T => {
  const body = response.data as { data?: T };
  return body?.data ?? (response.data as T);
};

class AppointmentHistoryImportService {
  private baseUrl = '/appointments/history-import';

  /**
   * Preview import — parse Excel and return unresolved terms for mapping
   */
  async previewImport(
    file: File,
    options?: AppointmentHistoryImportOptions
  ): Promise<ImportPreviewResult> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.companyId) {
      formData.append('companyId', String(options.companyId));
    }
    if (options?.autoCreateCatalogEntries) {
      formData.append('autoCreateCatalogEntries', String(options.autoCreateCatalogEntries));
    }
    if (options?.useAiExtraction) {
      formData.append('useAiExtraction', String(options.useAiExtraction));
    }

    const response = await apiClient.post(`${this.baseUrl}/preview`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return extractData(response);
  }

  /**
   * Import ASO/exam history from Excel
   */
  async importAppointmentsHistory(
    file: File,
    options?: AppointmentHistoryImportOptions
  ): Promise<{
    success: number;
    errors: Array<{ line: number; error: string }>;
    warnings?: Array<{ line: number; warning: string }>;
    importedRecords?: ImportedRecordRef[];
  }> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.companyId) {
      formData.append('companyId', String(options.companyId));
    }
    if (options?.mappings) {
      formData.append('mappings', JSON.stringify(options.mappings));
    }
    if (options?.autoCreateCatalogEntries) {
      formData.append('autoCreateCatalogEntries', String(options.autoCreateCatalogEntries));
    }
    if (options?.useAiExtraction) {
      formData.append('useAiExtraction', String(options.useAiExtraction));
    }

    const response = await apiClient.post(this.baseUrl, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return extractData(response);
  }

  /**
   * Download only the rows that would fail from the same spreadsheet/mappings, with a
   * "Motivo do Erro" column appended. Nothing is persisted.
   */
  async exportErrors(
    file: File,
    options?: AppointmentHistoryImportOptions
  ): Promise<Blob> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.companyId) {
      formData.append('companyId', String(options.companyId));
    }
    if (options?.mappings) {
      formData.append('mappings', JSON.stringify(options.mappings));
    }
    if (options?.useAiExtraction) {
      formData.append('useAiExtraction', String(options.useAiExtraction));
    }

    const response = await apiClient.post(`${this.baseUrl}/errors-export`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
    });

    return response as unknown as Blob;
  }

  /**
   * Import ASO/exam history from one or more PDFs (one document per record) — a IA extrai os
   * dados de cada arquivo, sem planilha. Cada arquivo já fica anexado ao registro criado.
   */
  async importFromPdfs(
    files: File[],
    options?: { companyId?: number; autoCreateCatalogEntries?: boolean }
  ): Promise<PdfImportResult[]> {
    const formData = new FormData();
    files.forEach(file => formData.append('files', file));
    if (options?.companyId) {
      formData.append('companyId', String(options.companyId));
    }
    if (options?.autoCreateCatalogEntries) {
      formData.append('autoCreateCatalogEntries', String(options.autoCreateCatalogEntries));
    }

    const response = await apiClient.post(`${this.baseUrl}/pdf`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return extractData(response);
  }

  /**
   * Attach a PDF to an already-imported Appointment (avulso — fora do fluxo de planilha)
   */
  async uploadHistoryDocument(appointmentId: number, file: File): Promise<{ filePath: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(`/appointments/${appointmentId}/history-document`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });

    return extractData(response);
  }

  /**
   * Download import template
   */
  async downloadTemplate(options?: { companyId?: number }): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (options?.companyId) {
      queryParams.append('companyId', String(options.companyId));
    }
    const query = queryParams.toString();

    const response = await apiClient.get(`${this.baseUrl}/template${query ? `?${query}` : ''}`, {
      responseType: 'blob',
    });

    return response as unknown as Blob;
  }
}

export const appointmentHistoryImportService = new AppointmentHistoryImportService();
export default appointmentHistoryImportService;
